'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Loader2, Plus, Send, X } from 'lucide-react';
import { supportChatApi, eventsApi } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';

type ChatStatus = 'open' | 'closed';

interface ChatListItem {
  id: string;
  status: ChatStatus;
  category?: string | null;
  organization?: { id: string; name: string } | null;
  event?: { id: string; title: string } | null;
  creator?: { id: string; name: string } | null;
  latest_message?: {
    id: string;
    message: string;
    created_at: string;
    sender?: { id: string; name: string } | null;
    sender_role: string;
  } | null;
  latestMessage?: {
    id: string;
    message: string;
    created_at: string;
    sender?: { id: string; name: string } | null;
    sender_role: string;
  } | null;
}

interface ChatMessage {
  id: string;
  message: string;
  sender_role: 'admin' | 'organizer';
  created_at: string;
  sender?: {
    id: string;
    name: string;
    avatar?: string | null;
  };
}

interface SupportChatLayoutProps {
  mode: 'organizer' | 'admin';
}

export function SupportChatLayout({ mode }: SupportChatLayoutProps) {
  const { user } = useAuthStore();
  const searchParams = useSearchParams();
  const initialEventId = searchParams?.get('eventId') ?? undefined;
  const [loadingChats, setLoadingChats] = useState(true);
  const [chats, setChats] = useState<ChatListItem[]>([]);
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New ticket modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalCategory, setModalCategory] = useState('General Support');
  const [modalMessage, setModalMessage] = useState('');
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [modalError, setModalError] = useState('');

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const activeChat = useMemo(
    () => chats.find((c) => c.id === activeChatId) ?? null,
    [chats, activeChatId]
  );

  // Load chat list
  useEffect(() => {
    let cancelled = false;
    const loadChats = async () => {
      setLoadingChats(true);
      setError(null);
      try {
        const res =
          mode === 'admin'
            ? await supportChatApi.adminChats()
            : await supportChatApi.myChats();
        const raw = res.data?.data;
        const data = raw?.data ?? raw ?? [];
        if (cancelled) return;
        const list: ChatListItem[] = Array.isArray(data) ? data : [];
        setChats(list);

        if (!activeChatId && list.length) {
          setActiveChatId(list[0].id);
        }

        // Auto-create chat if navigated from specific event support request
        if (!list.length && initialEventId && mode === 'organizer') {
          try {
            const eventRes = await eventsApi.showEvent(initialEventId);
            const event = eventRes.data?.data;
            const orgId = event?.organization_id ?? null;
            const category = 'Event Approval';
            const msg = `Hi admin, I need help with "${event?.title ?? 'this event'}".`;
            const createRes = await supportChatApi.createChat({
              organization_id: orgId,
              event_id: initialEventId,
              message: msg,
              category,
            });
            const newChat: ChatListItem = createRes.data?.data;
            setChats([newChat]);
            setActiveChatId(newChat.id);
          } catch {
          }
        }
      } catch {
        if (!cancelled) setError('Failed to load conversations');
      } finally {
        if (!cancelled) setLoadingChats(false);
      }
    };

    loadChats();

    return () => {
      cancelled = true;
    };
  }, [mode, initialEventId]);

  // Fetch messages for active chat & set up polling
  useEffect(() => {
    if (!activeChatId) {
      setMessages([]);
      return;
    }
    let cancelled = false;

    const fetchMessages = async () => {
      try {
        const res = await supportChatApi.getMessages(activeChatId);
        const data: ChatMessage[] = res.data?.data ?? [];
        if (!cancelled) {
          setMessages(data);
        }
      } catch {
      }
    };

    fetchMessages();
    const interval = setInterval(fetchMessages, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [activeChatId]);

  // Auto scroll to latest message
  useEffect(() => {
    if (messages.length > 0) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, activeChatId]);

  const handleSelectChat = (chatId: string) => {
    setActiveChatId(chatId);
  };

  const handleOpenNewTicketModal = () => {
    setModalCategory('General Support');
    setModalMessage('');
    setModalError('');
    setIsModalOpen(true);
  };

  const handleCreateTicketSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalMessage.trim()) {
      setModalError('Please enter your support message.');
      return;
    }
    setCreatingTicket(true);
    setModalError('');
    try {
      const res = await supportChatApi.createChat({
        category: modalCategory,
        message: modalMessage.trim(),
      });
      const newChat: ChatListItem = res.data?.data;
      if (newChat && newChat.id) {
        setChats((prev) => [newChat, ...prev]);
        setActiveChatId(newChat.id);
        setIsModalOpen(false);
        setModalMessage('');
      } else {
        throw new Error('Failed to create ticket.');
      }
    } catch (err: any) {
      setModalError(String(err?.response?.data?.message || err?.message || 'Could not start conversation.'));
    } finally {
      setCreatingTicket(false);
    }
  };

  const handleSend = async () => {
    if (!activeChatId || !input.trim() || sending) return;
    const textToSend = input.trim();
    setInput('');
    setSending(true);
    try {
      const res = await supportChatApi.sendMessage(activeChatId, {
        message: textToSend,
      });
      const newMsg: ChatMessage = res.data?.data;
      if (newMsg) {
        setMessages((prev) => [...prev, newMsg]);
        // Update latest message preview in chat list
        setChats((prev) =>
          prev.map((c) =>
            c.id === activeChatId
              ? {
                  ...c,
                  latestMessage: {
                    id: newMsg.id,
                    message: newMsg.message,
                    created_at: newMsg.created_at,
                    sender_role: newMsg.sender_role,
                  },
                }
              : c
          )
        );
      }
    } catch {
      setInput(textToSend); // Restore input on failure
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="bg-[#FDFBF7] border border-[#E5E2D9] rounded-2xl overflow-hidden shadow-sm relative h-full flex-1 flex flex-col min-h-0">
      <div className="grid grid-cols-1 md:grid-cols-[300px,minmax(0,1fr)] lg:grid-cols-[340px,minmax(0,1fr)] h-full min-h-0 flex-1">
        
        {/* Chat List Sidebar */}
        <div className="border-r border-[#E5E2D9] flex flex-col bg-[#F9F7F0] h-full min-h-0 overflow-hidden">
          <div className="p-5 border-b border-[#E5E2D9] bg-[#F5EFE6] shrink-0">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-[#5F6F52] text-white flex items-center justify-center shadow-xs">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-[#2D3536] tracking-tight">
                    {mode === 'admin' ? 'Support Inbox' : 'Support & Help'}
                  </h3>
                  <p className="text-xs font-medium text-[#6E7B68]">
                    EnthiosHub Team
                  </p>
                </div>
              </div>

              {mode === 'organizer' && (
                <button
                  type="button"
                  onClick={handleOpenNewTicketModal}
                  className="w-9 h-9 rounded-xl bg-[#5F6F52] hover:bg-[#4E5D42] text-white flex items-center justify-center transition-colors shadow-xs shrink-0 cursor-pointer"
                  title="Start new conversation"
                >
                  <Plus className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto min-h-0 custom-scrollbar p-3 space-y-1.5">
            {loadingChats ? (
              <div className="flex flex-col items-center justify-center h-full gap-2 text-[#6E7B68]">
                <Loader2 className="w-6 h-6 animate-spin text-[#5F6F52]" />
                <p className="text-xs font-semibold">Loading chats...</p>
              </div>
            ) : chats.length === 0 ? (
              <div className="p-6 text-center space-y-3 my-auto">
                <div className="w-12 h-12 bg-[#5F6F52]/10 rounded-full flex items-center justify-center mx-auto text-[#5F6F52]">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <p className="text-sm font-bold text-[#2D3536]">
                  No support conversations yet
                </p>
                <p className="text-xs text-[#6E7B68] leading-relaxed">
                  Need assistance with your events or account? Start a message with our team.
                </p>
                {mode === 'organizer' && (
                  <button
                    type="button"
                    onClick={handleOpenNewTicketModal}
                    className="mt-2 px-4 py-2.5 bg-[#5F6F52] hover:bg-[#4E5D42] text-white rounded-xl text-xs font-semibold transition-all inline-flex items-center gap-2 shadow-xs cursor-pointer"
                  >
                    <Plus className="w-4 h-4" />
                    Start a conversation
                  </button>
                )}
              </div>
            ) : (
              chats.map((chat) => {
                const latest = chat.latestMessage ?? chat.latest_message;
                const isActive = chat.id === activeChatId;
                return (
                  <button
                    key={chat.id}
                    type="button"
                    onClick={() => handleSelectChat(chat.id)}
                    className={`w-full text-left p-3.5 rounded-xl transition-all border cursor-pointer ${
                      isActive
                        ? 'bg-[#5F6F52] text-white border-[#5F6F52] shadow-xs'
                        : 'bg-[#FDFBF7] text-[#2D3536] border-[#E5E2D9] hover:bg-[#F2EFE6]'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <p className={`text-xs font-bold truncate ${isActive ? 'text-white' : 'text-[#2D3536]'}`}>
                        {chat.category || chat.event?.title || chat.organization?.name || 'General Support'}
                      </p>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-md font-semibold capitalize shrink-0 ${
                          isActive
                            ? 'bg-white/20 text-white'
                            : chat.status === 'closed'
                            ? 'bg-gray-100 text-gray-500 border border-gray-200'
                            : 'bg-[#5F6F52]/10 text-[#5F6F52] border border-[#5F6F52]/20'
                        }`}
                      >
                        {chat.status}
                      </span>
                    </div>
                    {latest?.message && (
                      <p className={`text-xs truncate ${isActive ? 'text-white/80' : 'text-[#6E7B68]'}`}>
                        {latest.message}
                      </p>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Chat Window Panel */}
        <div className="flex flex-col bg-[#FDFBF7] h-full min-h-0 overflow-hidden flex-1">
          {/* Header */}
          <div className="px-6 py-4 border-b border-[#E5E2D9] bg-[#F9F7F0] flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#5F6F52]/10 text-[#5F6F52] flex items-center justify-center font-bold">
                <MessageCircle className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-base font-bold text-[#2D3536]">
                  {activeChat
                    ? activeChat.category || activeChat.event?.title || activeChat.organization?.name || 'Support Conversation'
                    : 'Support Chat'}
                </h2>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className={`w-2 h-2 rounded-full ${activeChat?.status === 'closed' ? 'bg-gray-400' : 'bg-emerald-500'}`} />
                  <p className="text-xs font-medium text-[#6E7B68]">
                    {activeChat?.status === 'closed' ? 'Ticket Resolved & Closed' : 'Active Channel • Live support'}
                  </p>
                </div>
              </div>
            </div>

            {mode === 'admin' && activeChat && activeChat.status === 'open' && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    await supportChatApi.closeChat(activeChat.id);
                    setChats((prev) =>
                      prev.map((c) => (c.id === activeChat.id ? { ...c, status: 'closed' } : c))
                    );
                  } catch {
                  }
                }}
                className="px-4 py-2 rounded-xl border border-red-200 text-red-600 text-xs font-semibold hover:bg-red-50 transition-colors shadow-xs cursor-pointer"
              >
                Resolve Ticket
              </button>
            )}
          </div>

          {/* Messages Scroll Container */}
          <div className="flex-1 overflow-y-auto min-h-0 p-4 sm:p-6 space-y-4 custom-scrollbar bg-[#F9F7F0]/40">
            {!activeChat ? (
              <div className="h-full flex flex-col items-center justify-center text-center p-6 text-[#6E7B68]">
                <MessageCircle className="w-14 h-14 text-[#5F6F52]/40 mb-3" />
                <p className="text-base font-bold text-[#2D3536]">No conversation selected</p>
                <p className="text-xs mt-1 max-w-xs leading-relaxed">
                  Select a chat from the sidebar or click the plus button to open a new support request.
                </p>
              </div>
            ) : messages.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-[#6E7B68] italic text-xs">
                No messages in this conversation yet.
              </div>
            ) : (
              <AnimatePresence initial={false}>
                {messages.map((msg) => {
                  const isCurrentUser =
                    msg.sender?.id && user?.id
                      ? msg.sender.id === user.id
                      : mode === 'organizer'
                        ? msg.sender_role !== 'admin'
                        : msg.sender_role === 'admin';

                  return (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[75%] sm:max-w-[65%] rounded-2xl px-4 py-3 shadow-xs ${
                          isCurrentUser
                            ? 'bg-[#5F6F52] text-white rounded-tr-none'
                            : 'bg-white border border-[#E2DFD5] text-[#2D3536] rounded-tl-none'
                        }`}
                      >
                        <p className="text-sm font-medium leading-relaxed whitespace-pre-wrap break-words">
                          {msg.message}
                        </p>
                        <div
                          className={`mt-2 flex items-center justify-between gap-4 text-[10px] font-semibold ${
                            isCurrentUser ? 'text-white/75' : 'text-[#6E7B68]'
                          }`}
                        >
                          <span>
                            {msg.sender?.name ?? (msg.sender_role === 'admin' ? 'EnthiosHub Support' : 'Organizer')}
                          </span>
                          <span>
                            {new Date(msg.created_at).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Composer Input Footer */}
          <div className="p-4 bg-[#F9F7F0] border-t border-[#E5E2D9]">
            <div className="flex items-end gap-3">
              <div className="flex-1 relative">
                <textarea
                  ref={textareaRef}
                  rows={2}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    activeChat?.status === 'closed'
                      ? 'This support ticket has been resolved and closed.'
                      : 'Type your message... (Press Enter to send, Shift+Enter for new line)'
                  }
                  disabled={!activeChatId || activeChat?.status === 'closed'}
                  className="w-full resize-none text-sm px-4 py-3 rounded-xl border border-[#E2DFD5] bg-[#FDFBF7] text-[#2D3536] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#5F6F52]/20 focus:border-[#5F6F52] transition-all disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-xs"
                />
              </div>

              <button
                type="button"
                onClick={handleSend}
                disabled={!input.trim() || sending || !activeChatId || activeChat?.status === 'closed'}
                className="h-12 px-5 rounded-xl bg-[#5F6F52] hover:bg-[#4E5D42] text-white text-xs font-bold uppercase tracking-wider transition-all flex items-center justify-center gap-2 shadow-xs disabled:opacity-40 cursor-pointer shrink-0"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <span>Send</span>
                    <Send className="w-3.5 h-3.5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* New Support Ticket Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="w-full max-w-lg bg-[#FDFBF7] border border-[#E5E2D9] rounded-2xl p-6 shadow-xl relative"
            >
              <div className="flex items-center justify-between pb-4 border-b border-[#E5E2D9] mb-5">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-[#5F6F52]/10 text-[#5F6F52] flex items-center justify-center font-bold">
                    <MessageCircle className="w-4 h-4" />
                  </div>
                  <h3 className="text-lg font-bold text-[#2D3536]">New Support Ticket</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="text-[#6E7B68] hover:text-[#2D3536] p-1 rounded-lg hover:bg-[#F2EFE6] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {modalError && (
                <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs font-semibold">
                  {modalError}
                </div>
              )}

              <form onSubmit={handleCreateTicketSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-[#2D3536] mb-1.5 uppercase tracking-wider">
                    Category / Topic
                  </label>
                  <select
                    value={modalCategory}
                    onChange={(e) => setModalCategory(e.target.value)}
                    className="w-full h-11 rounded-xl bg-[#F9F7F0] border border-[#E2DFD5] px-3.5 text-sm text-[#2D3536] focus:outline-none focus:ring-2 focus:ring-[#5F6F52]/20 focus:border-[#5F6F52] font-medium"
                  >
                    <option value="General Support">General Support</option>
                    <option value="Event Approval">Event Approval & Publishing</option>
                    <option value="Technical Issue">Technical Issue</option>
                    <option value="Account & Billing">Account & Settlements</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-bold text-[#2D3536] mb-1.5 uppercase tracking-wider">
                    Message
                  </label>
                  <textarea
                    rows={4}
                    value={modalMessage}
                    onChange={(e) => setModalMessage(e.target.value)}
                    placeholder="Describe how the EnthiosHub support team can help you..."
                    className="w-full rounded-xl bg-[#F9F7F0] border border-[#E2DFD5] p-3.5 text-sm text-[#2D3536] placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#5F6F52]/20 focus:border-[#5F6F52] font-medium"
                    required
                  />
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2.5 rounded-xl border border-[#E2DFD5] text-[#6E7B68] text-xs font-semibold hover:bg-[#F2EFE6] transition-colors cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={creatingTicket || !modalMessage.trim()}
                    className="px-5 py-2.5 rounded-xl bg-[#5F6F52] hover:bg-[#4E5D42] text-white text-xs font-bold transition-all flex items-center gap-2 shadow-xs disabled:opacity-50 cursor-pointer"
                  >
                    {creatingTicket ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      'Start Conversation'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
